import React from 'react';
import { useDashboard } from '../DashboardContext';

interface Props {
    compareMode: boolean;
    prevPeriodLabel: string;
}

const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const SummaryCards: React.FC<Props> = ({ compareMode, prevPeriodLabel }) => {
    const { summary, loading } = useDashboard();

    if (loading) {
        return (
            <div className="row g-3 mb-4">
                {[1, 2, 3].map(i => (
                    <div key={i} className="col-md-4">
                        <div className="card h-100">
                            <div className="card-body">
                                <div className="placeholder-glow">
                                    <span className="placeholder col-6 mb-2 d-block"></span>
                                    <span className="placeholder col-4 mb-2 d-block" style={{ height: 40 }}></span>
                                    <span className="placeholder col-8 d-block"></span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const cards = [
        {
            label: 'ACTIVE USERS THIS PERIOD',
            icon: '👤',
            value: summary?.activeUsers ?? 0,
            borderColor: '#2563eb',       // blue
            subtext: 'unique alumni + students with any activity',
            scrollTarget: 'section-other-actions',
        },
        {
            label: 'CAMPAIGN INTERACTIONS',
            icon: '📩',
            value: summary?.campaignInteractions ?? 0,
            borderColor: '#0891b2',       // teal/info
            subtext: 'opens + clicks, all campaigns',
            scrollTarget: 'section-campaigns',
        },
        {
            label: 'OTHER ACTIONS',
            icon: '⚡',
            value: summary?.otherActions ?? 0,
            borderColor: '#7c3aed',       // purple
            subtext: 'logins, visits, events, contributions',
            scrollTarget: 'section-other-actions',
        },
    ];

    return (
        <div className="row g-3 mb-4">
            {cards.map((card, idx) => (
                <div key={idx} className="col-md-4">
                    <div
                        className="card h-100"
                        onClick={() => scrollTo(card.scrollTarget)}
                        style={{
                            borderTop: `3px solid ${card.borderColor}`,
                            cursor: 'pointer',
                            transition: 'box-shadow 0.15s ease',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)')}
                        onMouseLeave={e => (e.currentTarget.style.boxShadow = '')}
                    >
                        <div className="card-body position-relative">
                            {/* Icon — top-right, decorative */}
                            <span className="position-absolute top-0 end-0 mt-2 me-3 fs-3 opacity-25">
                                {card.icon}
                            </span>

                            {/* Label — small caps muted */}
                            <div className="text-uppercase text-muted small fw-bold mb-1" style={{ letterSpacing: '0.05em' }}>
                                {card.label}
                            </div>

                            {/* Value — large bold */}
                            <div className="fw-bold mb-1" style={{ fontSize: 32 }}>
                                {card.value.toLocaleString()}
                            </div>

                            {/* Sub-text */}
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <small className="text-muted">{card.subtext}</small>
                            </div>

                            {/* Compare row — dashed top border, visible only in Compare mode */}
                            {compareMode && (
                                <div className="mt-2 pt-2 d-flex align-items-center gap-2 flex-wrap"
                                    style={{ borderTop: '1px dashed #dee2e6' }}>
                                    <small className="fw-bold text-muted text-uppercase">{prevPeriodLabel}:</small>
                                    <small className="text-muted">—</small>
                                    <small className="text-muted">no prior period data</small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default SummaryCards;
